import { DealsApiService } from './crm/DealsApiService.js';
import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { mapDealFromApi, mapDealToApi, mapCommissionSplitToApi, mapDealContactRoleToApi, normalizeStageToApi } from './crm/ApiEntityMapper.js';
import { generateId, nowIso } from '../utils/IdUtils.js';
import { STAGE_META } from '../constants/Stages.js';
import {
    computeDealCompleteness,
    getDealCommissionPct,
    getDealCommissionAmount,
    buildKanbanSortValues
} from '../utils/KanbanDealSort.js';
import { CommissionService } from './CommissionService.js';
import { auth } from './AuthService.js';
import { AuditService } from './AuditService.js';
import { RevenueSetupService } from './RevenueSetupService.js';
import { parseDealDetail } from '../../schemas/deal.js';

import { RevenueScheduleService } from './RevenueScheduleService.js';
import { ApiModeDealService } from './ApiModeDealService.js';
import { UserService } from './UserService.js';
import { paymentModelToFrequency } from '../constants/PaymentModels.js';

export class DealService {
    static async getAll() {
        return CrmApiRepository.getAllDeals();
    }

    /** Table/Kanban/dashboard projection — no child graph. Shared list-view cache. */
    static async getListView() {
        return ApiModeDealService.getAllEnriched();
    }

    static async updateStage(dealId, newStage) {
        // Lightweight GET only — full getById also loads milestones/schedules/billing
        // and multiplies API+DB load on every kanban drag.
        const row = await DealsApiService.getById(dealId);
        const deal = mapDealFromApi(row);
        if (!deal) throw new Error('Deal not found');
        
        // Check required fields for Closed Won
        if (newStage === 'closed') {
            const missing = [];
            if (!deal.contractValue || deal.contractValue <= 0) missing.push('contractValue');
            const hasExpectedCloseDate = (deal.expectedCloseDate || '').trim() && !Number.isNaN(Date.parse(deal.expectedCloseDate));
            if (!hasExpectedCloseDate) missing.push('expectedCloseDate');
            if (missing.length > 0) {
                const error = new Error(`Missing required fields: ${missing.join(', ')}`);
                error.missingFields = missing;
                error.dealId = dealId;
                throw error;
            }
        }
        if (newStage === 'closedLost') {
            const missing = [];
            if (!(deal.lostReason || '').trim()) missing.push('lostReason');
            if (!(deal.lostReasonCategory || deal.lost_reason_category || '').trim()) missing.push('lostReasonCategory');
            if (missing.length > 0) {
                const error = new Error(`Lost reason required: ${missing.join(', ')}`);
                error.missingFields = missing;
                error.dealId = dealId;
                error.intendedStage = newStage;
                throw error;
            }
        }
        
        const apiStage = normalizeStageToApi(newStage);
        let status = 'open';
        if (apiStage === 'closed_won') status = 'won';
        if (apiStage === 'closed_lost') status = 'lost';
        // Sync probability to the new stage's minimum, matching form behavior
        const newProbability = STAGE_META[newStage]?.probabilityMin ?? deal.probability;
        // Suppress before the API call so the WS echo cannot race ahead of the
        // local success toast (HTTP response often returns after the broadcast).
        window._suppressRealtimeRefreshFor?.('deal.updated', dealId);
        window._suppressRealtimeRefreshFor?.('deal.stage_changed', dealId);
        window._suppressRealtimeToastFor?.('deal.updated', dealId);
        window._suppressRealtimeToastFor?.('deal.stage_changed', dealId);
        await DealsApiService.update(dealId, {
            deal_stage: apiStage,
            status,
            probability: newProbability,
            version: deal.version
        });
        window.dispatchEvent(new CustomEvent('dealSaved', { detail: { dealId } }));
    }

    static async getById(id) {
        const bundle = await DealsApiService.getBundle(id);
        const bundleData = bundle?.data ?? bundle;

        const deal = mapDealFromApi(bundleData.deal);

        // --- Milestones ---
        if (bundleData.milestones?.error) {
            console.warn('DealService.getById: milestones section failed:', bundleData.milestones.error);
        }
        deal.dealMilestones = (bundleData.milestones?.data || []).map(m => ({
            title: m.milestone_title || m.title || '',
            dueDate: m.due_date || m.dueDate || '',
            amount: Number(m.amount) || 0,
            notes: m.notes || '',
            paymentTerms: m.payment_terms || m.paymentTerms || '',
        }));

        // --- Custom Schedules ---
        if (bundleData.custom_schedules?.error) {
            console.warn('DealService.getById: custom_schedules section failed:', bundleData.custom_schedules.error);
        }
        deal.customScheduleRows = (bundleData.custom_schedules?.data || []).map(s => ({
            invoiceDate: s.invoice_date || s.invoiceDate || '',
            amount: Number(s.amount) || 0,
            paymentTerms: s.payment_terms || s.paymentTerms || '',
            notes: s.notes || '',
        }));

        // --- Billing Details ---
        if (bundleData.billing_details?.error) {
            console.warn('DealService.getById: billing_details section failed:', bundleData.billing_details.error);
        }
        const billingData = bundleData.billing_details?.data ?? null;
        if (billingData) {
            deal.paymentModel = billingData.payment_model || deal.paymentModel;
            deal.paymentTerms = billingData.payment_terms || deal.paymentTerms;
            deal.paymentCount = billingData.total_invoices != null ? Number(billingData.total_invoices) : deal.paymentCount;
            deal.totalInvoices = billingData.total_invoices != null ? Number(billingData.total_invoices) : deal.totalInvoices;
            deal.taxIncluded = billingData.tax_enabled === true || billingData.tax_enabled === 1;
            deal.taxRate = billingData.tax_rate != null ? Number(billingData.tax_rate) : 0;
            deal.taxAmount = billingData.tax_amount != null ? Number(billingData.tax_amount) : 0;
            deal.totalWithTax = billingData.total_with_tax != null ? Number(billingData.total_with_tax) : 0;
            deal.retainerAmount = billingData.retainer_amount != null ? Number(billingData.retainer_amount) : (deal.retainerAmount ?? 0);
            deal.retainerPercent = billingData.retainer_percent != null ? Number(billingData.retainer_percent) : null;
            deal.estimatedAnnualValue = billingData.estimated_annual_value != null
                ? Number(billingData.estimated_annual_value)
                : (deal.estimatedAnnualValue ?? 0);
            deal.ongoingStartDate = billingData.ongoing_start_date
                || billingData.revenue_start_date
                || deal.ongoingStartDate
                || '';
            deal.ongoingEndDate = billingData.ongoing_end_date
                || billingData.revenue_end_date
                || deal.ongoingEndDate
                || '';
            deal.minCommitment = billingData.min_commitment != null ? Number(billingData.min_commitment) : (deal.minCommitment ?? 0);
            deal.maxBudget = billingData.max_budget != null ? Number(billingData.max_budget) : (deal.maxBudget ?? 0);
            if (billingData.payment_model === 'ongoing' || deal.paymentModel === 'ongoing') {
                const freq = billingData.invoice_frequency || '';
                deal.ongoingInvoiceFrequency = freq === 'yearly' || freq === 'annual'
                    ? 'annual'
                    : (freq === 'quarterly' ? 'quarterly' : (freq || deal.ongoingInvoiceFrequency || 'monthly'));
                deal.invoiceFrequency = freq || deal.invoiceFrequency;
            }
        } else {
            // No billing details row — set deterministic safe defaults.
            deal.taxIncluded = false;
            deal.taxRate = 0;
            deal.taxAmount = 0;
            deal.totalWithTax = 0;
        }

        // --- Commission Config ---
        // Corrected logic: when no config row exists, deal is eligible with no preset rate.
        // Rate is left as null (blank field in UI) — no hardcoded fallback value.
        if (bundleData.commission_config?.error) {
            console.warn('DealService.getById: commission_config section failed:', bundleData.commission_config.error);
        }
        const configData = bundleData.commission_config?.data ?? null;
        if (configData) {
            deal.commissionEligible = String(configData.commission_eligible ?? 'true') !== 'false';
            deal.commissionModel = configData.commission_model || 'percentage';
            deal.commissionRate = configData.commission_rate != null && configData.commission_rate !== ''
                ? Number(configData.commission_rate)
                : null;
            deal.commissionFixedAmount = configData.commission_fixed_amount != null && configData.commission_fixed_amount !== ''
                ? Number(configData.commission_fixed_amount)
                : null;
            deal.commissionBasis = configData.commission_basis || 'schedule';
            deal.payoutMethod = configData.payout_method || 'per_schedule';
        } else {
            // No config row: eligible by default, no preset rate or fixed amount.
            deal.commissionEligible = true;
            deal.commissionModel = deal.commissionModel || 'percentage';
            deal.commissionRate = null;
            deal.commissionFixedAmount = null;
            deal.commissionBasis = 'schedule';
            deal.payoutMethod = 'per_schedule';
        }

        if (!deal.customScheduleRows?.length && (deal.paymentCount || deal.totalInvoices)) {
            console.warn('DealService.getById: customScheduleRows is empty despite having paymentCount:', deal.paymentCount, '— custom_schedules section may have returned no data');
        }
        return parseDealDetail(deal);
    }

    static async getDealContacts() {
        return [];
    }

    static async enrichDeal(deal, clients, users, contacts) {
        const client = clients.find(c => c.id === deal.clientId);
        const owner = users.find(u => u.id === deal.ownerUserId);
        const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
        const primaryContact = deal.primaryContactId
            ? contacts.find(c => c.id === deal.primaryContactId)
            : null;
        return {
            ...deal,
            dealName: deal.dealName || deal.name,
            accountName: client?.name || deal.accountName || '',
            dealOwner: owner?.name || deal.dealOwner || '',
            clientName: client?.name || '',
            ownerName: owner?.name || '',
            primaryContactName: primaryContact ? `${primaryContact.firstName} ${primaryContact.lastName}`.trim() : '',
            accountRating: client?.rating || '',
            completenessScore: computeDealCompleteness(deal),
            commissionPct: getDealCommissionPct(deal),
            commissionAmount: getDealCommissionAmount(deal),
            createdByName: userMap[deal.createdBy] || '—',
            updatedByName: userMap[deal.updatedBy] || '—',
            _kanbanSort: buildKanbanSortValues({
                ...deal,
                accountRating: client?.rating || ''
            })
        };
    }

    static async getAllEnriched() {
        return ApiModeDealService.getAllEnriched();
    }

    static computeForecast(amount, probability) {
        return Math.round((amount || 0) * (probability || 0) / 100);
    }

    static async save(deal, contactIds = [], primaryContactId = null) {
        const ts = nowIso();
        const stage = deal.dealStage || deal.stage || 'qualification';
        const prob = deal.probability != null ? Number(deal.probability) : (STAGE_META[stage]?.probability ?? 0);
        const amount = Number(deal.amount ?? deal.dealAmount ?? 0);
        const session = auth.getSession();

        let status = deal.status || 'open';
        if (stage === 'closed' || stage === 'closed_won') status = 'won';
        if (stage === 'closedLost' || stage === 'closed_lost') status = 'lost';
        if (status === 'lost' && !deal.lostReason?.trim()) {
            throw new Error('Lost reason is required when closing a deal as lost.');
        }

        const prev = deal.id ? await DealService.getById(deal.id) : null;

        // Optimize: Only fetch clients/users if needed for ID resolution
        if (!deal.clientId && deal.accountName) {
            // Use cached clients instead of fetching all
            const clients = await CrmApiRepository.getAllClients();
            const account = clients.find(c => c.name === deal.accountName);
            if (account) deal.clientId = account.id;
        }
        if (!deal.ownerUserId && deal.dealOwner) {
            // Use cached users instead of fetching all
            const users = await UserService.getLookupUsers().catch(() => []);
            const owner = users.find(u => u.name === deal.dealOwner);
            if (owner) deal.ownerUserId = owner.id;
        }

        const dealName = deal.dealName?.trim() || deal.name?.trim();
        if (!dealName) throw new Error('Deal name is required.');
        if (!deal.clientId) throw new Error('Account is required.');

        const apiPayload = mapDealToApi({
            ...deal,
            dealName,
            dealStage: stage,
            status,
            amount,
            contractValue: Number(deal.contractValue ?? deal.dealAmount ?? amount),
            probability: prob,
            createdBy: deal.createdBy || session?.userId,
            version: prev?.version ?? deal.version
        }, session?.userId, { isUpdate: !!deal.id });

        let saved;
        if (deal.id) {
            window._suppressRealtimeToastFor?.('deal.updated', deal.id);
            window._suppressRealtimeToastFor?.('deal.stage_changed', deal.id);
            window._suppressRealtimeRefreshFor?.('deal.updated', deal.id);
            window._suppressRealtimeRefreshFor?.('deal.stage_changed', deal.id);
            saved = await DealsApiService.update(deal.id, apiPayload);
        } else {
            saved = await DealsApiService.create(apiPayload);
            const createdId = saved?.id;
            if (createdId) {
                window._suppressRealtimeToastFor?.('deal.created', createdId);
                window._suppressRealtimeRefreshFor?.('deal.created', createdId);
            }
        }
        const dealId = saved?.id || deal.id;

        // Calculate expected_first_invoice_date from recurring, ongoing, or one-time rows
        const recurringRows = deal.recurringScheduleRows || [];
        const ongoingRows = deal.ongoingScheduleRows || [];
        const hasRecurringRows = recurringRows.length > 0 && recurringRows.some(r => r.date);
        const hasOngoingRows = ongoingRows.length > 0 && ongoingRows.some(r => r.date);
        const scheduleRows = hasOngoingRows ? ongoingRows : recurringRows;
        const hasScheduleRows = hasOngoingRows || hasRecurringRows;
        const firstInvoiceDate = hasScheduleRows ? (scheduleRows[0]?.date || null) : (deal.oneTimeDate || deal.expectedFirstInvoiceDate || deal.revenueStartDate || null);
        const lastInvoiceDate = hasScheduleRows ? (scheduleRows[scheduleRows.length - 1]?.date || null) : (deal.oneTimeDate || deal.lastInvoiceDate || deal.revenueEndDate || null);

        // Persist billing details, milestones, and custom schedules in parallel
        const persistPromises = [];
        
        if (dealId && (
            deal.paymentModel ||
            deal.invoiceFrequency ||
            deal.paymentTerms ||
            deal.totalInvoices ||
            deal.paymentCount ||
            (deal.dealMilestones?.length) ||
            deal.taxIncluded === true ||
            deal.taxRate != null ||
            deal.paymentModel === 'ongoing' ||
            deal.retainerAmount != null ||
            deal.estimatedAnnualValue != null ||
            deal.ongoingStartDate ||
            deal.ongoingEndDate
        )) {
            const milestoneTerms = Array.isArray(deal.dealMilestones)
                ? deal.dealMilestones.find(m => (m?.paymentTerms || '').trim())?.paymentTerms
                : null;

            const paymentTermsToPersist = (deal.paymentTerms || milestoneTerms || '').trim() || null;

            const isOngoing = deal.paymentModel === 'ongoing';
            const ongoingFreqRaw = deal.ongoingInvoiceFrequency || 'monthly';
            const ongoingFreqApi = ongoingFreqRaw === 'annual' ? 'yearly' : ongoingFreqRaw;
            const invoiceFrequency = isOngoing
                ? ongoingFreqApi
                : (deal.invoiceFrequency || paymentModelToFrequency(deal.paymentModel || 'monthly'));
            const revenueStart = isOngoing
                ? (deal.ongoingStartDate || firstInvoiceDate)
                : firstInvoiceDate;
            const revenueEnd = isOngoing
                ? (deal.ongoingEndDate || lastInvoiceDate)
                : lastInvoiceDate;

            const billingPromise = DealsApiService.upsertBillingDetails(dealId, {
                payment_model: deal.paymentModel || 'monthly',
                invoice_frequency: invoiceFrequency,
                invoice_amount: deal.invoiceAmount != null ? Number(deal.invoiceAmount) : null,
                total_invoices: deal.paymentCount != null ? Number(deal.paymentCount) : (deal.totalInvoices != null ? Number(deal.totalInvoices) : null),
                payment_terms: paymentTermsToPersist,
                expected_first_invoice_date: isOngoing ? (deal.ongoingStartDate || firstInvoiceDate) : firstInvoiceDate,
                last_invoice_date: isOngoing ? (deal.ongoingEndDate || lastInvoiceDate) : lastInvoiceDate,
                revenue_start_date: revenueStart,
                revenue_end_date: revenueEnd,
                tax_enabled: deal.taxIncluded === true,
                tax_rate: deal.taxRate != null ? Number(deal.taxRate) : 0,
                retainer_amount: isOngoing ? (deal.retainerAmount != null ? Number(deal.retainerAmount) : 0) : null,
                retainer_percent: isOngoing && deal.retainerPercent != null ? Number(deal.retainerPercent) : null,
                estimated_annual_value: isOngoing ? (deal.estimatedAnnualValue != null ? Number(deal.estimatedAnnualValue) : 0) : null,
                ongoing_start_date: isOngoing ? (deal.ongoingStartDate || null) : null,
                ongoing_end_date: isOngoing ? (deal.ongoingEndDate || null) : null,
                min_commitment: null,
                max_budget: null,
            }).catch(e => {
                console.warn('Failed to persist billing details:', e);
                return null;
            });
            persistPromises.push(billingPromise);
        }

        const milestones = deal.dealMilestones || [];
        if (dealId && milestones.length > 0) {
            const mapped = milestones.map((m, i) => ({
                milestone_title: m.milestoneTitle || m.title || `Milestone ${i + 1}`,
                due_date: m.dueDate || m.date || null,
                amount: Number(m.amount) || 0,
                notes: m.notes || null,
                payment_terms: m.paymentTerms || null,
                sort_order: m.sortOrder != null ? m.sortOrder : i,
            }));
            const milestonesPromise = DealsApiService.replaceMilestones(dealId, mapped).catch(e => {
                console.warn('Failed to persist milestones:', e);
                return null;
            });
            persistPromises.push(milestonesPromise);
        }

        let customRows = deal.customScheduleRows || [];
        const oneTimeDate = deal.oneTimeDate || null;
        const oneTimeAmount = deal.oneTimeAmount || 0;
        const isRecurringModel = ['monthly', 'weekly', 'quarterly', 'annual'].includes(deal.paymentModel);
        const isOngoingModel = deal.paymentModel === 'ongoing';
        const hasRows = customRows.length > 0 || recurringRows.length > 0 || ongoingRows.length > 0 || (deal.paymentModel === 'one_time' && oneTimeDate);
        if (dealId && hasRows) {
            // For one-time, store as a single custom schedule row
            if (deal.paymentModel === 'one_time' && oneTimeDate && !customRows.length && !recurringRows.length && !ongoingRows.length) {
                customRows = [{ invoiceDate: oneTimeDate, amount: oneTimeAmount }];
            }
            const mapScheduleRow = (r, i) => ({
                invoice_date: r.invoiceDate || r.date || null,
                amount: Number(r.amount) || 0,
                notes: r.notes || null,
                sort_order: r.sortOrder != null ? r.sortOrder : i,
            });
            const isFilled = (r) => Boolean(r.invoiceDate || r.date) || Number(r.amount) > 0;
            const mappedCustom = customRows.filter(isFilled).map(mapScheduleRow);
            const mappedRecurring = recurringRows.filter(isFilled).map(mapScheduleRow);
            const mappedOngoing = ongoingRows.filter(isFilled).map(mapScheduleRow);
            // Recurring, ongoing, and custom are alternative schedule sources — never merge
            // leftover empty placeholder rows from the unused editor into the save.
            const allRows = isRecurringModel
                ? mappedRecurring
                : isOngoingModel
                    ? mappedOngoing
                    : (mappedCustom.length ? mappedCustom : mappedRecurring);
            if (allRows.length > 0) {
                const schedulesPromise = DealsApiService.replaceCustomSchedules(dealId, allRows).catch(e => {
                    console.warn('Failed to persist custom schedules:', e);
                    return null;
                });
                persistPromises.push(schedulesPromise);
            }
        }

        const splits = Array.isArray(deal.commissionSplits) ? deal.commissionSplits : [];
        if (dealId && deal.commissionEligible !== false) {
            const mappedSplits = splits
                .filter((s) => (s.userId || s.contactId) && Number(s.splitPct) > 0)
                .map(mapCommissionSplitToApi)
                .filter(Boolean);
            const splitsPromise = DealsApiService.replaceCommissionSplits(dealId, mappedSplits).catch((e) => {
                console.warn('Failed to persist commission splits:', e);
                return null;
            });
            persistPromises.push(splitsPromise);
        } else if (dealId && deal.commissionEligible === false) {
            // Clear splits when commission is turned off
            persistPromises.push(
                DealsApiService.replaceCommissionSplits(dealId, []).catch((e) => {
                    console.warn('Failed to clear commission splits:', e);
                    return null;
                })
            );
        }

        const contactRoles = Array.isArray(deal.dealContactRoles) ? deal.dealContactRoles : [];
        if (dealId) {
            const mappedRoles = contactRoles
                .map((r, i) => mapDealContactRoleToApi(r, i))
                .filter(Boolean);
            persistPromises.push(
                DealsApiService.replaceContactRoles(dealId, mappedRoles).catch((e) => {
                    console.warn('Failed to persist deal contact roles:', e);
                    return null;
                })
            );
        }

        // Persist commission config only when a config already exists OR the user explicitly
        // interacted with the commission section. Skipping prevents accidental row creation
        // from deals saved for unrelated changes.
        if (dealId && !deal._skipCommission) {
            const configPayload = {
                commission_eligible: deal.commissionEligible === false ? 'false' : 'true',
                commission_model: deal.commissionModel || 'percentage',
                commission_basis: deal.commissionBasis || deal.commission_basis || 'schedule',
                payout_method: deal.payoutMethod || deal.payout_method || 'per_schedule',
            };
            const rate = deal.commissionRate;
            if (rate != null && rate !== '') configPayload.commission_rate = String(rate);
            const fixed = deal.commissionFixedAmount;
            if (fixed != null && fixed !== '') configPayload.commission_fixed_amount = String(fixed);
            persistPromises.push(
                DealsApiService.upsertCommissionConfig(dealId, configPayload).catch((e) => {
                    console.warn('Failed to persist commission config:', e);
                    return null;
                })
            );
        }

        // Execute all persistence operations in parallel
        await Promise.all(persistPromises);

        const record = {
            ...mapDealFromApi(saved),
            ...deal,
            id: saved?.id || deal.id,
            dealName,
            name: dealName,
            clientId: deal.clientId,
            ownerUserId: deal.ownerUserId || session?.userId,
            dealStage: stage,
            dealPipeline: deal.dealPipeline || 'digital_product',
            amount,
            dealAmount: amount,
            contractValue: Number(deal.contractValue ?? deal.dealAmount ?? amount),
            probability: prob,
            forecastAmount: DealService.computeForecast(amount, prob),
            status,
            primaryContactId: primaryContactId || deal.primaryContactId || null,
            updatedAt: ts,
            createdAt: prev?.createdAt || deal.createdAt || ts
        };

        // Run audit and commission operations in parallel with schedule fetch
        const postSavePromises = [];
        
        if (prev) {
            postSavePromises.push(AuditService.logFieldChanges({
                entityType: 'deal',
                entityId: record.id,
                action: 'deal.fields_changed',
                before: prev,
                after: record,
                fields: ['ownerUserId', 'dealStage', 'status', 'probability', 'contractValue']
            }));
        } else {
            postSavePromises.push(AuditService.log({
                entityType: 'deal',
                entityId: record.id,
                action: 'deal.created',
                after: { dealName: record.dealName, clientId: record.clientId }
            }));
        }

        if (status === 'won' && prev?.status !== 'won') {
            postSavePromises.push(CommissionService.createForDeal(record));
            postSavePromises.push(AuditService.log({
                entityType: 'deal',
                entityId: record.id,
                action: 'deal.closed_won',
                after: { dealStage: record.dealStage, contractValue: record.contractValue }
            }));
        }

        if (status === 'lost' && prev?.status !== 'lost') {
            postSavePromises.push(AuditService.log({
                entityType: 'deal',
                entityId: record.id,
                action: 'deal.closed_lost',
                after: { lostReason: deal.lostReason }
            }));
        }

        // Notify listeners (list refresh, cache invalidation) immediately after
        // the deal is saved. The deal is already committed in the backend, so the
        // deals list must refresh now — independent of any downstream side effect
        // (audit/commission/schedule) that may fail afterwards.
        window.dispatchEvent(new CustomEvent('dealSaved', { detail: { deal: record } }));

        // Run audit, commission, and schedule side-effects as best-effort work.
        // A failure here must NOT prevent the deal from appearing in the list.
        const schedulesPromise = RevenueScheduleService.getByDeal(record.id);
        Promise.all([...postSavePromises, schedulesPromise])
            .then(([_, schedules]) => {
                const setup = RevenueSetupService.computeStatus(record, schedules);
                record.revenueSetupStatus = setup.status;
            })
            .catch(err => console.error('Post-save side effects failed (deal still saved):', err));

        return record;
    }

    static async syncDealContacts() {
        return null;
    }

    static async delete(id) {
        if (!id) return;

        // Best-effort prefetch: delete should not depend on read endpoints.
        let prev = null;
        try {
            prev = await DealService.getById(id);
        } catch (err) {
            console.warn('DealService.delete: prefetch failed, continuing with delete:', err);
        }

        window._suppressRealtimeToastFor?.('deal.deleted', id);
        window._suppressRealtimeRefreshFor?.('deal.deleted', id);
        await DealsApiService.delete(id);

        // Keep local audit non-blocking so API success is reflected immediately.
        AuditService.log({
            entityType: 'deal',
            entityId: id,
            action: 'deal.archived',
            before: { status: prev?.status || null },
            after: { status: 'archived' }
        }).catch(err => {
            console.warn('DealService.delete: audit logging failed (delete already succeeded):', err);
        });

        window.dispatchEvent(new CustomEvent('dealDeleted', { detail: { id } }));
    }
}
